export class ApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

function buildUrl(path: string): string {
  const base = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '')
  return `${base}${path}`
}

async function getToken(): Promise<string> {
  const { useAuthStore } = await import('@/stores/auth')
  const token = useAuthStore().accessToken
  if (!token) throw new Error('Not authenticated')
  return token
}

async function handleUnauthorized(): Promise<void> {
  const [{ useAuthStore }, { default: router }] = await Promise.all([
    import('@/stores/auth'),
    import('@/router'),
  ])
  useAuthStore().clearSession()
  void router.push('/login')
}

async function request<T>(
  url: string,
  method: string,
  extraHeaders: Record<string, string> = {},
  body?: BodyInit,
  signal?: AbortSignal,
): Promise<T> {
  const token = await getToken()

  const resp = await fetch(url, {
    method,
    headers: { Authorization: `Bearer ${token}`, ...extraHeaders },
    body,
    signal,
  })

  if (resp.status === 401) {
    await handleUnauthorized()
    throw new ApiError('Session expired. Please log in again.', 401)
  }

  if (!resp.ok) {
    let errBody: { detail?: string | { code?: string } } = {}
    try { errBody = await resp.json() as typeof errBody } catch { /* ignore */ }
    const detail = errBody.detail
    let message: string
    if (typeof detail === 'string') {
      message = detail
    } else if (detail && typeof detail === 'object') {
      const knownMessages: Record<string, string> = {
        'not-owner': "This account isn't authorized for this portal.",
        'napi-auth-failed': 'Session expired. Please sign in again.',
        'bluelink-auth-failed': 'Hyundai login failed — the backend refresh token may need to be rotated. Check the API Log page.',
        'bluelink-unavailable': 'Hyundai service is temporarily unavailable — try again shortly.',
        'bluelink-error': "Couldn't reach Hyundai — try again shortly.",
        'car-unreachable': "Car didn't respond in time — it may be asleep. Try again shortly.",
        'refresh-already-in-flight': 'A refresh is already in progress — please wait.',
        'db-query-timeout': 'The server is taking longer than usual — try again in a moment.',
        'db-pool-saturated': 'The server is busy — try again in a moment.',
      }
      message = (detail.code && knownMessages[detail.code]) ?? `Request failed (${resp.status})`
    } else if (resp.status === 502) {
      message = 'Service is temporarily unavailable — try again shortly.'
    } else if (resp.status === 503) {
      message = 'Service is temporarily unavailable — try again shortly.'
    } else if (resp.status === 504) {
      message = 'The server took too long to respond — try again shortly.'
    } else {
      message = `Request failed (${resp.status})`
    }
    throw new ApiError(message, resp.status)
  }

  try {
    return await resp.json() as T
  } catch {
    return undefined as unknown as T
  }
}

export const apiClient = {
  get<T>(path: string, signal?: AbortSignal): Promise<T> {
    return request<T>(buildUrl(path), 'GET', {}, undefined, signal)
  },

  post<T>(path: string, body?: unknown): Promise<T> {
    const hasBody = body !== undefined
    return request<T>(
      buildUrl(path),
      'POST',
      hasBody ? { 'Content-Type': 'application/json' } : {},
      hasBody ? JSON.stringify(body) : undefined,
    )
  },

  async getOrNull<T>(path: string, signal?: AbortSignal): Promise<T | null> {
    try {
      return await request<T>(buildUrl(path), 'GET', {}, undefined, signal)
    } catch (e) {
      if (e instanceof ApiError && (e.status === 404 || e.status === 422)) {
        return null
      }
      throw e
    }
  },
}
